import { Organization, OrganizationMember } from '@prisma/client'
import { useMemo } from 'react'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { useChatContext } from '../../../context/chat.context'
import { useParticipantsTyping } from '../../Thread/hooks/useParticipantsTyping'

type Props = {
  conversationId?: string
  organization: Organization
  organizationMember: OrganizationMember
  organizationMembers: OrganizationMemberFromContext[]
}

export const useData = ({
  conversationId,
  organization,
  organizationMember,
  organizationMembers,
}: Props) => {
  const { conversations, findByOrganizationMember, refresh, countRefresh } =
    useChatContext()

  const { typing } = useParticipantsTyping({
    conversationIds: conversations.map(conversation => conversation.id),
    organization,
    organizationMembers,
    onNewMessage: payload => {
      const canIgnore = conversationId === payload.conversationId

      if (canIgnore) {
        return
      }

      refresh()
    },
    onUnknownConversation: () => {
      refresh()
    },
  })

  const items = useMemo(() => {
    return organizationMembers
      .filter(member => member.appSessionId)
      .map(member => {
        const conversation = findByOrganizationMember(member.id)

        const participantUser = conversation?.participants?.find(
          item => item.organizationMemberId === organizationMember.id,
        )

        const key = conversation?.id ?? member?.id
        const messageLast = conversation?.messages?.[0]
        const countNotifications = participantUser?.countUnreadMessages ?? 0

        return {
          key,
          member,
          conversation,
          messageLast,
          countNotifications,
        }
      })
  }, [organizationMembers, conversations])

  return {
    items,
    conversations,
    typing,
  }
}
